import { apiClient } from '../../core/api';

const padTwo = (value: number) => String(value).padStart(2, '0');

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const formatLocalDateTime = (date: Date): string =>
  `${formatLocalDate(date)}T${padTwo(date.getHours())}:${padTwo(date.getMinutes())}:${padTwo(date.getSeconds())}`;

const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatUtcDate = (date: Date): string =>
  `${date.getUTCFullYear()}-${padTwo(date.getUTCMonth() + 1)}-${padTwo(date.getUTCDate())}`;

const parseUtcDate = (dateString: string): Date => new Date(`${dateString}T00:00:00Z`);

/**
 * Parse timestamp from backend.
 * - If has timezone indicator (Z or +/-HH:MM), parse as UTC/specified timezone
 * - If no timezone indicator, treat as user's local time (backend now returns local time)
 */
const parseTimestamp = (value: string): Date => {
  const normalized = value.replace(' ', 'T');
  // new Date() without 'Z' suffix treats the string as local time
  return new Date(normalized);
};

// Legacy alias for backward compatibility
const parseUtcTimestamp = parseTimestamp;

const toUtcDateString = (dateString: string): string => {
  const local = parseLocalDate(dateString);
  return local.toISOString().slice(0, 10);
};

const toUtcDateTime = (dateString: string, endOfDay: boolean = false): string => {
  const date = parseLocalDate(dateString);
  if (endOfDay) {
    date.setHours(23, 59, 59, 0);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString().replace('.000Z', 'Z');
};

const toUtcDateTimeRange = (
  startDate: string,
  endDate: string
): { startTime: string; endTime: string } => {
  const start = parseLocalDate(startDate);
  start.setHours(0, 0, 0, 0);
  const end = parseLocalDate(endDate);
  end.setDate(end.getDate() + 1);
  end.setHours(0, 0, 0, 0);
  return { startTime: start.toISOString(), endTime: end.toISOString() };
};

/**
 * Get user's timezone from browser
 * Returns IANA timezone identifier (e.g., "Australia/Sydney", "Asia/Shanghai")
 */
const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export interface TimeSeriesDataPoint {
  date: string;
  value: number;
  formattedDate: string;
  recordCount?: number; // For engagement time data
}

export interface SupportMetricsData {
  avgTimeFirstResponse: number;
  timeSeriesData: TimeSeriesDataPoint[];
  trend?: number;
}

export interface QuestionCountData {
  resolvedConversationsData: TimeSeriesDataPoint[];
  resolutionRateData: TimeSeriesDataPoint[];
  totalConversations: number;
  totalResolvedConversations: number;
  averageResolutionRate: number;
  resolvedTrend?: number;
  rateTrend?: number;
}

export interface EngagementTimeData {
  timeSeriesData: TimeSeriesDataPoint[];
  averageEngagementTime: number;
  totalSessions: number;
  trend?: number;
}

/**
 * What a metric that could not be fetched renders as: empty series and zeroes, i.e. the same as a
 * genuinely quiet store. Used only when its SIBLINGS succeeded — if all three fail the caller
 * throws, because "everything is zero" and "nothing loaded" must not look identical.
 */
const EMPTY_SUPPORT_METRICS: SupportMetricsData = {
  avgTimeFirstResponse: 0,
  timeSeriesData: [],
};

const EMPTY_QUESTION_COUNT: QuestionCountData = {
  resolvedConversationsData: [],
  resolutionRateData: [],
  totalConversations: 0,
  totalResolvedConversations: 0,
  averageResolutionRate: 0,
};

const EMPTY_ENGAGEMENT_TIME: EngagementTimeData = {
  timeSeriesData: [],
  averageEngagementTime: 0,
  totalSessions: 0,
};

export interface DateRange {
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
}

export interface SupportPerformanceFilters extends DateRange {
  granularity?: 'day' | 'hour' | '15min';
  userId: string;
  timezone?: string; // IANA timezone identifier (e.g., "Australia/Sydney")
}

// GraphQL Response Types
interface DashboardEtlSessionAggregates {
  average: {
    spendTime: string;
    __typename: string;
  };
  keys: string[];
  __typename: string;
}

/**
 * PostGraphile 在连接上同时给两种聚合：
 *  - `aggregates`      整个过滤集合的一次聚合（无分组）
 *  - `groupedAggregates` 按 groupBy 分桶后的逐桶聚合
 * 总平均必须用前者。用后者再求平均就是 average-of-averages —— 桶大小不均时算错。
 */
interface DashboardEtlSessionOverallAggregates {
  average: {
    spendTime: string | null;
    __typename?: string;
  };
  __typename?: string;
}

interface DashboardEtlSessionsConnection {
  aggregates?: DashboardEtlSessionOverallAggregates | null;
  groupedAggregates: DashboardEtlSessionAggregates[];
  __typename: string;
}

/**
 * 人工处理耗时：从人工接入（assigned_at）到人工收工（resolved_at）。
 *
 * recordCount 是判断「要不要显示这根柱子」的依据 —— 样本为 0 时应当隐藏，而不是显示 0。
 * 数据侧的函数在无匹配时返回的是一行 avgSeconds=null / recordCount=0（avg + count 没有
 * GROUP BY，恒返回一行），不是空数组。
 */
export interface HumanHandlingTimeData {
  avgSeconds: number;
  recordCount: number;
}

interface HumanHandlingTimeGraphQLResponse {
  humanHandlingTime?: {
    nodes: Array<{ avgSeconds: string | null; recordCount: string }>;
  } | null;
}

interface AvgTimeFirstResponseGraphQLResponse {
  data: {
    dashboardEtlSessions: DashboardEtlSessionsConnection;
  };
}

interface ResolvedConversionCountRecord {
  periodStart: string; // Both daily and hourly use periodStart from resolvedConversionCount
  resolvedCount: string;
  unResolvedCount: string;
  totalCount: string;
  __typename: string;
}

interface ResolvedConversionCountConnection {
  nodes: ResolvedConversionCountRecord[];
  __typename: string;
}

interface QuestionCountGraphQLResponse {
  data: {
    // Now both daily and hourly use resolvedConversionCount for consistent datetime handling
    resolvedConversionCount?: ResolvedConversionCountConnection;
  };
}

interface EngagementTimeRecord {
  periodStart: string;
  avgSpendTime: string;
  recordCount: string;
  __typename: string;
}

interface EngagementTimeConnection {
  nodes: EngagementTimeRecord[];
  __typename: string;
}

interface EngagementTimeGraphQLResponse {
  data: {
    sessionEngagementV2: EngagementTimeConnection;
  };
}

export class SupportPerformanceService {
  /**
   * Get average time to first response metrics
   * Uses GraphQL: QueryAvgTimeFirstResponse or QueryAvgTimeFirstResponseHour
   */
  /**
   * 人工从接入到收工的平均耗时。喂给 Average resolution time 图上的 Human 柱子。
   *
   * 那根柱子此前是硬编码的 6 分钟常量（ComparisonBarChart 的 HUMAN_TIME_SECONDS = 360），
   * 因为算它需要「人工什么时候开始、什么时候结束」—— 而 resolved_at 在上游此前根本没有
   * 写入方、assigned_at 也只有仪表盘那条路径写。数据侧补齐后才有了这个查询。
   *
   * **参数名是 tenantId，不是 userId。** dashboard_etl_ticket 的列名就是 user_id，函数参数
   * 同名会让 `where user_id = user_id` 把裸引用解析成列、变成恒真的同义反复。其余五个函数
   * 用 userId 只是因为它们的列叫 tenant_id，天然错开。
   *
   * 窗口用 toUtcDateTimeRange 的半开区间 [start, 次日 00:00)，与函数里的
   * `assigned_at < end_time` 对应 —— toUtcDateTime(date, true) 给的是 23:59:59，配 `<`
   * 会漏掉当天最后一秒。
   */
  static async getHumanHandlingTime(
    filters: SupportPerformanceFilters
  ): Promise<HumanHandlingTimeData> {
    const empty: HumanHandlingTimeData = { avgSeconds: 0, recordCount: 0 };
    try {
      const { startTime, endTime } = toUtcDateTimeRange(filters.startDate, filters.endDate);

      const response = await apiClient.graphql<HumanHandlingTimeGraphQLResponse>(
        {
          operationName: 'QueryHumanHandlingTime',
          variables: { startTime, endTime, tenantId: filters.userId },
          query: `query QueryHumanHandlingTime($startTime: Datetime!, $endTime: Datetime!, $tenantId: String!) {
              humanHandlingTime(startTime: $startTime, endTime: $endTime, tenantId: $tenantId) {
                nodes {
                  avgSeconds
                  recordCount
                  __typename
                }
                __typename
              }
            }`,
        },
        '/graphql'
      );

      const node = response.data?.humanHandlingTime?.nodes?.[0];
      if (!node) {
        return empty;
      }
      const recordCount = parseInt(node.recordCount, 10) || 0;
      // avgSeconds 为 null 只在 recordCount = 0 时出现，但两个条件都判一下：
      // 显示 0 秒的人工耗时比隐藏柱子更糟，那是个看起来合理的假数字。
      const avgSeconds =
        recordCount > 0 && node.avgSeconds !== null ? parseFloat(node.avgSeconds) || 0 : 0;
      return { avgSeconds, recordCount };
    } catch (error) {
      // 这根柱子是对照用的，取不到不该让整张卡片挂掉 —— 隐藏它就行。
      console.error('Failed to fetch human handling time:', error);
      return empty;
    }
  }

  static async getAvgTimeFirstResponse(
    filters: SupportPerformanceFilters
  ): Promise<SupportMetricsData> {
    try {
      const isHourlyData = filters.granularity === 'hour';

      // Use greaterThanOrEqualTo/lessThanOrEqualTo with full datetime to align with other APIs
      const startDateTime = toUtcDateTime(filters.startDate);
      const endDateTime = toUtcDateTime(filters.endDate, true);

      const baseVariables = {
        filter: {
          sessionStartTime: {
            greaterThanOrEqualTo: startDateTime,
            lessThanOrEqualTo: endDateTime,
          },
          // `processed: { equalTo: 'OK' }` used to sit here and made EVERY request 400:
          //   Variable "$filter" got invalid value …; Field "processed" is not defined by type
          //   "DashboardEtlSessionFilter"
          // (dev console, 2026-08-11). The field does not exist on that input type — the schema
          // either dropped it or never had it — and GraphQL rejects the whole document when a
          // variable carries an unknown input field, so the avg-first-response metric could never
          // load for anyone. Removed rather than guessed at a replacement: filtering on a field
          // the schema does not define is not a filter anyone can honour.
        },
        condition: {
          tenantId: filters.userId,
        },
      };

      // 一次查询就够：`aggregates` 给整区间的总平均，`groupedAggregates` 给时间序列。
      // 改动前 hour 粒度要发两次查询（hour 给曲线、day 给总平均），那是为了绕开
      // average-of-averages —— 而它并没有绕开，只是把「按小时求均值的均值」换成
      // 「按天求均值的均值」。连接级 aggregates 让第二次查询彻底多余。
      const groupBy = isHourlyData
        ? 'SESSION_START_TIME_TRUNCATED_TO_HOUR'
        : 'SESSION_START_TIME_TRUNCATED_TO_DAY';

      const response = await apiClient.graphql<AvgTimeFirstResponseGraphQLResponse['data']>(
        {
          operationName: isHourlyData
            ? 'QueryAvgTimeFirstResponseHour'
            : 'QueryAvgTimeFirstResponse',
          variables: baseVariables,
          query: `query ${
            isHourlyData ? 'QueryAvgTimeFirstResponseHour' : 'QueryAvgTimeFirstResponse'
          }($filter: DashboardEtlSessionFilter!, $condition: DashboardEtlSessionCondition!) {
              dashboardEtlSessions(filter: $filter, condition: $condition) {
                aggregates {
                  average {
                    spendTime
                    __typename
                  }
                  __typename
                }
                groupedAggregates(groupBy: ${groupBy}) {
                  average {
                    spendTime
                    __typename
                  }
                  keys
                  __typename
                }
                __typename
              }
            }`,
        },
        '/graphql'
      );

      return this.transformAvgTimeFirstResponse({ data: response.data }, isHourlyData);
    } catch (error) {
      console.error('Failed to fetch avg time first response:', error);
      throw new Error('Failed to load average time to first response data');
    }
  }

  /**
   * Get resolved conversations and resolution rate metrics
   * Uses GraphQL: GetQuestionCountByDay or GetQuestionCountByHour
   */
  static async getQuestionCountMetrics(
    filters: SupportPerformanceFilters
  ): Promise<QuestionCountData> {
    try {
      const isHourlyData = filters.granularity === 'hour';
      const operationName = isHourlyData ? 'GetQuestionCountByHour' : 'GetQuestionCountByDay';
      const timezone = filters.timezone || getUserTimezone();

      let payload;

      if (isHourlyData) {
        // Convert date to datetime format for hourly queries
        const startDateTime = toUtcDateTime(filters.startDate);
        const endDateTime = toUtcDateTime(filters.endDate, true);

        payload = {
          operationName,
          variables: {
            startTime: startDateTime,
            endTime: endDateTime,
            userId: filters.userId,
            userTimezone: timezone,
          },
          query: `query ${operationName}($startTime: Datetime!, $endTime: Datetime!, $userId: String!, $userTimezone: String!) {
            resolvedConversionCount(
              granularity: "hour"
              startTime: $startTime
              endTime: $endTime
              userId: $userId
              userTimezone: $userTimezone
            ) {
              nodes {
                periodStart
                resolvedCount
                unResolvedCount
                totalCount
                __typename
              }
              __typename
            }
          }`,
        };
      } else {
        // Use resolvedConversionCount with datetime format for consistent behavior
        // This uses timestamp type in backend SQL with >= and <= operators
        const startDateTime = toUtcDateTime(filters.startDate);
        const endDateTime = toUtcDateTime(filters.endDate, true);

        payload = {
          operationName,
          variables: {
            startTime: startDateTime,
            endTime: endDateTime,
            userId: filters.userId,
            userTimezone: timezone,
          },
          query: `query ${operationName}($startTime: Datetime!, $endTime: Datetime!, $userId: String!, $userTimezone: String!) {
            resolvedConversionCount(
              granularity: "day"
              startTime: $startTime
              endTime: $endTime
              userId: $userId
              userTimezone: $userTimezone
            ) {
              nodes {
                periodStart
                resolvedCount
                unResolvedCount
                totalCount
                __typename
              }
              __typename
            }
          }`,
        };
      }

      const response = await apiClient.graphql<QuestionCountGraphQLResponse['data']>(
        payload,
        '/graphql'
      );

      return this.transformQuestionCountData({ data: response.data }, isHourlyData);
    } catch (error) {
      console.error('Failed to fetch question count metrics:', error);
      throw new Error('Failed to load conversation metrics data');
    }
  }

  /**
   * Get engagement time metrics
   * Uses GraphQL: EngagementTimesV2ByDay, EngagementTimesV2ByHour or EngagementTimesV2By15mins
   */
  static async getEngagementTimeMetrics(
    filters: SupportPerformanceFilters
  ): Promise<EngagementTimeData> {
    try {
      const isMinuteData = filters.granularity === '15min';
      const isHourlyData = filters.granularity === 'hour';
      let granularity: string;
      let operationName: string;

      if (isMinuteData) {
        granularity = '15min';
        operationName = 'EngagementTimesV2By15mins';
      } else if (isHourlyData) {
        granularity = 'hour';
        operationName = 'EngagementTimesV2ByHour';
      } else {
        granularity = 'day';
        operationName = 'EngagementTimesV2ByDay';
      }

      const { startTime, endTime } = toUtcDateTimeRange(filters.startDate, filters.endDate);
      const timezone = filters.timezone || getUserTimezone();

      const payload = {
        operationName,
        variables: {
          granularity,
          startTime,
          endTime,
          userId: filters.userId,
          userTimezone: timezone,
        },
        query: `query ${operationName}($granularity: String!, $startTime: Datetime!, $endTime: Datetime!, $userId: String!, $userTimezone: String!) {
          sessionEngagementV2(
            granularity: $granularity
            startTime: $startTime
            endTime: $endTime
            userId: $userId
            userTimezone: $userTimezone
          ) {
            nodes {
              periodStart
              avgSpendTime
              recordCount
              __typename
            }
            __typename
          }
        }`,
      };

      const response = await apiClient.graphql<EngagementTimeGraphQLResponse['data']>(
        payload,
        '/graphql'
      );

      return this.transformEngagementTimeData(
        { data: response.data },
        isMinuteData || isHourlyData
      );
    } catch (error) {
      console.error('Failed to fetch engagement time metrics:', error);
      throw new Error('Failed to load engagement time data');
    }
  }

  /**
   * Get all support performance metrics in one call
   */
  static async getAllSupportMetrics(filters: SupportPerformanceFilters): Promise<{
    avgTimeFirstResponse: SupportMetricsData;
    questionCount: QuestionCountData;
    engagementTime: EngagementTimeData;
  }> {
    // allSettled, NOT all: these are three independent queries against the analytics layer, and
    // Promise.all made ONE failing query blank the whole sheet — dev 2026-08-11 showed the red
    // "Failed to load support performance data" banner over four zeroed cards while the other two
    // queries had answered fine. A metric we could not fetch should cost that metric, not the page.
    const [avgTime, questionCount, engagementTime] = await Promise.allSettled([
      this.getAvgTimeFirstResponse(filters),
      this.getQuestionCountMetrics(filters),
      this.getEngagementTimeMetrics(filters),
    ]);

    const failures = [avgTime, questionCount, engagementTime].filter(r => r.status === 'rejected');
    if (failures.length === 3) {
      // Everything failed: that IS a page-level failure, and the first reason is the useful one.
      const reason = (failures[0] as PromiseRejectedResult).reason;
      console.error('Failed to fetch all support metrics:', reason);
      throw new Error(
        reason instanceof Error ? reason.message : 'Failed to load support performance data'
      );
    }
    failures.forEach(f =>
      console.error('Support metric unavailable:', (f as PromiseRejectedResult).reason)
    );

    return {
      avgTimeFirstResponse: avgTime.status === 'fulfilled' ? avgTime.value : EMPTY_SUPPORT_METRICS,
      questionCount:
        questionCount.status === 'fulfilled' ? questionCount.value : EMPTY_QUESTION_COUNT,
      engagementTime:
        engagementTime.status === 'fulfilled' ? engagementTime.value : EMPTY_ENGAGEMENT_TIME,
    };
  }

  /**
   * Transform GraphQL response for average time to first response
   * @param response - 同一次查询里既有 `aggregates`（总平均）也有 `groupedAggregates`（时间序列）
   * @param isHourlyData - Whether the data is hourly
   */
  private static transformAvgTimeFirstResponse(
    response: AvgTimeFirstResponseGraphQLResponse,
    isHourlyData: boolean = false
  ): SupportMetricsData {
    // Optional-chained like insightsService: dev's GraphQL layer can answer with an envelope
    // carrying neither data nor errors for unclassified tenants — that is "no rows", not a crash.
    const aggregates = response.data?.dashboardEtlSessions?.groupedAggregates ?? [];

    if (!aggregates || aggregates.length === 0) {
      return {
        avgTimeFirstResponse: 0,
        timeSeriesData: [],
        trend: 0,
      };
    }

    // 总平均取连接级 `aggregates` —— 整个过滤集合的一次 avg(spend_time)。
    //
    // 改动前是「Σ每日均值 ÷ 天数」，即 average-of-averages：把只有 1 个会话的那天和有
    // 10 个会话的那天等权重对待。注释曾声称「换用日粒度就避免了这个问题」，但那只是把
    // 按小时求均值的均值换成按天求均值的均值，问题没变。
    //
    // 实测（prod 某租户 67 个会话 / 51 天）：旧算法 12.31s、正确 12.03s —— 只差 0.28s，
    // 因为 51 天里 41 天各只有 1 个会话，桶大小接近均匀。误差取决于数据分布，业务量涨上来
    // 或者某天集中来一批会话就会放大。
    const overallAverageMs = response.data?.dashboardEtlSessions?.aggregates?.average?.spendTime;
    const avgTimeFirstResponse = overallAverageMs ? parseFloat(overallAverageMs) / 1000 : 0;

    // 这里原先还有一句「若 > 9 秒则减 3 秒（adjustment for system overhead）」。已删除：
    //  1. 那是个凭空的修正因子，没有任何依据说明这 3 秒是什么；
    //  2. 它是对着旧的 Phoenix 口径（span 内部耗时）调出来的，而现在 spend_time 是旁写记的
    //     「顾客发问 → 回复落库」，含排队和网络往返 —— 口径已经变了；
    //  3. 上面那组实测里，−22.6% 的总误差有 −3.00s 来自这一句，只有 +0.28s 来自
    //     average-of-averages。它才是主因。

    // Transform to time series data (spendTime is in milliseconds, convert to seconds)
    const timeSeriesData: TimeSeriesDataPoint[] = aggregates
      .map(agg => {
        const dateStr = agg.keys[0];
        const date = new Date(dateStr);
        // 同上：曲线上的每个点原先也被减了 3 秒，所以整条曲线都是偏移的。
        const spendTime = parseFloat(agg.average.spendTime) / 1000; // Convert to seconds

        return {
          // For hourly data, keep the full local datetime to preserve hour info
          date: isHourlyData ? formatLocalDateTime(date) : formatLocalDate(date),
          value: spendTime,
          formattedDate: isHourlyData
            ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Calculate trend (simple comparison between first and last values)
    const trend =
      timeSeriesData.length >= 2
        ? ((timeSeriesData[timeSeriesData.length - 1].value - timeSeriesData[0].value) /
            timeSeriesData[0].value) *
          100
        : 0;

    return {
      avgTimeFirstResponse,
      timeSeriesData,
      trend: parseFloat(trend.toFixed(1)),
    };
  }

  /**
   * Transform GraphQL response for question count data
   */
  private static transformQuestionCountData(
    response: QuestionCountGraphQLResponse,
    isHourlyData: boolean = false
  ): QuestionCountData {
    // Now both daily and hourly use resolvedConversionCount for consistent datetime handling
    const connection = response.data?.resolvedConversionCount;
    const nodes = connection?.nodes;

    if (!nodes || nodes.length === 0) {
      return {
        resolvedConversationsData: [],
        resolutionRateData: [],
        totalConversations: 0,
        totalResolvedConversations: 0,
        averageResolutionRate: 0,
        resolvedTrend: 0,
        rateTrend: 0,
      };
    }

    // Sort by periodStart (both daily and hourly now use periodStart)
    const sortedNodes = nodes.sort((a, b) => {
      const dateA = a.periodStart || '';
      const dateB = b.periodStart || '';
      return parseUtcTimestamp(dateA).getTime() - parseUtcTimestamp(dateB).getTime();
    });

    // Transform to time series data
    const resolvedConversationsData: TimeSeriesDataPoint[] = sortedNodes.map(node => {
      const rawDate = node.periodStart || '';
      const dateObj = parseUtcTimestamp(rawDate);
      // For hourly data, keep the full ISO string to preserve hour info
      const dateString = isHourlyData ? formatLocalDateTime(dateObj) : formatLocalDate(dateObj);

      return {
        date: dateString,
        value: parseInt(node.resolvedCount, 10),
        formattedDate: isHourlyData
          ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
          : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      };
    });

    const resolutionRateData: TimeSeriesDataPoint[] = sortedNodes.map(node => {
      const resolved = parseInt(node.resolvedCount, 10);
      const total = parseInt(node.totalCount, 10);
      const rate = total > 0 ? (resolved / total) * 100 : 0;
      const rawDate = node.periodStart || '';
      const dateObj = parseUtcTimestamp(rawDate);
      // For hourly data, keep the full ISO string to preserve hour info
      const dateString = isHourlyData ? formatLocalDateTime(dateObj) : formatLocalDate(dateObj);

      return {
        date: dateString,
        value: rate,
        formattedDate: isHourlyData
          ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
          : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      };
    });

    // Calculate totals and averages
    const totalResolvedConversations = sortedNodes.reduce(
      (sum, node) => sum + parseInt(node.resolvedCount, 10),
      0
    );

    const totalConversations = sortedNodes.reduce(
      (sum, node) => sum + parseInt(node.totalCount, 10),
      0
    );

    const averageResolutionRate =
      totalConversations > 0 ? (totalResolvedConversations / totalConversations) * 100 : 0;

    // Calculate trends
    const resolvedTrend =
      resolvedConversationsData.length >= 2
        ? ((resolvedConversationsData[resolvedConversationsData.length - 1].value -
            resolvedConversationsData[0].value) /
            Math.max(resolvedConversationsData[0].value, 1)) *
          100
        : 0;

    const rateTrend =
      resolutionRateData.length >= 2
        ? resolutionRateData[resolutionRateData.length - 1].value - resolutionRateData[0].value
        : 0;

    return {
      resolvedConversationsData,
      resolutionRateData,
      totalConversations,
      totalResolvedConversations,
      averageResolutionRate: parseFloat(averageResolutionRate.toFixed(1)),
      resolvedTrend: parseFloat(resolvedTrend.toFixed(1)),
      rateTrend: parseFloat(rateTrend.toFixed(1)),
    };
  }

  /**
   * Format time value for display (seconds to readable format)
   */
  static formatTimeValue(seconds: number): string {
    if (seconds < 60) {
      return `${seconds.toFixed(1)} sec`;
    } else if (seconds < 3600) {
      const minutes = Math.round((seconds / 60) * 10) / 10;
      return `${minutes} min`;
    } else {
      const hours = Math.round((seconds / 3600) * 10) / 10;
      return `${hours} hr`;
    }
  }

  /**
   * Get predefined date ranges
   */
  static getDateRangePresets(): { [key: string]: DateRange } {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    return {
      today: {
        startDate: formatLocalDate(today),
        endDate: formatLocalDate(today),
      },
      yesterday: {
        startDate: formatLocalDate(yesterday),
        endDate: formatLocalDate(yesterday),
      },
      last7Days: {
        startDate: formatLocalDate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)),
        endDate: formatLocalDate(today),
      },
      last30Days: {
        startDate: formatLocalDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)),
        endDate: formatLocalDate(today),
      },
      last3Months: {
        startDate: formatLocalDate(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)),
        endDate: formatLocalDate(today),
      },
      last12Months: {
        startDate: formatLocalDate(new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000)),
        endDate: formatLocalDate(today),
      },
    };
  }

  /**
   * Determine appropriate granularity based on date range
   */
  static getOptimalGranularity(startDate: string, endDate: string): 'day' | 'hour' | '15min' {
    const start = parseUtcDate(startDate);
    const end = parseUtcDate(endDate);
    const diffInDays = Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    // Only same day (diffInDays < 1) uses hourly data
    if (diffInDays < 1) {
      return 'hour'; // Same day use hourly data
    } else {
      return 'day'; // 2+ days use daily data
    }
  }

  /**
   * Transform GraphQL response for engagement time data
   */
  private static transformEngagementTimeData(
    response: EngagementTimeGraphQLResponse,
    isTimeBasedData: boolean = false // true for hour or 15min data
  ): EngagementTimeData {
    const nodes = response.data?.sessionEngagementV2?.nodes;

    if (!nodes || nodes.length === 0) {
      return {
        timeSeriesData: [],
        averageEngagementTime: 0,
        totalSessions: 0,
        trend: 0,
      };
    }

    // Sort by period start
    const sortedNodes = nodes.sort(
      (a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime()
    );

    // Transform to time series data (avgSpendTime is in milliseconds, convert to seconds)
    const timeSeriesData: TimeSeriesDataPoint[] = sortedNodes.map(node => {
      const avgSpendTime = (parseFloat(node.avgSpendTime) || 0) / 1000; // Convert to seconds
      const recordCount = parseInt(node.recordCount) || 0;

      // Parse periodStart as UTC and convert to local time for display
      let dateString: string;
      let formattedDate: string;

      if (isTimeBasedData) {
        // For hourly/15min data, preserve time info
        const dateObj = parseUtcTimestamp(node.periodStart);
        dateString = formatLocalDateTime(dateObj);
        formattedDate = dateObj.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      } else {
        // For daily data, convert UTC periodStart to local date
        const dateObj = parseUtcTimestamp(node.periodStart);
        dateString = formatLocalDate(dateObj);
        formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }

      return {
        date: dateString,
        value: avgSpendTime,
        formattedDate,
        recordCount: recordCount,
      };
    });

    // Calculate totals and weighted average
    const totalSessions = sortedNodes.reduce(
      (sum, node) => sum + (parseInt(node.recordCount) || 0),
      0
    );

    // Use weighted average formula: Σ(avgSpendTime * recordCount) / Σ(recordCount)
    // This correctly calculates the overall average instead of "average of averages"
    const weightedTotalSpendTime = sortedNodes.reduce((sum, node) => {
      const avgTime = parseFloat(node.avgSpendTime) || 0;
      const count = parseInt(node.recordCount) || 0;
      return sum + avgTime * count;
    }, 0);

    const averageEngagementTime =
      totalSessions > 0 ? weightedTotalSpendTime / totalSessions / 1000 : 0;

    // Calculate trend
    const trend =
      timeSeriesData.length >= 2
        ? ((timeSeriesData[timeSeriesData.length - 1].value - timeSeriesData[0].value) /
            Math.max(timeSeriesData[0].value, 1)) *
          100
        : 0;

    return {
      timeSeriesData,
      averageEngagementTime,
      totalSessions,
      trend: parseFloat(trend.toFixed(1)),
    };
  }
}
